import type { UserModel } from '@/models/user';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import UserService from '@/services/userService';
import { SidebarLinkIdentifiers } from '@/components/sidebar/LinkIdentifiers';
import type { SidebarLinkModel } from '@/components/sidebar/interfaces/SidebarLinkModel';
import { LoadingStateModel } from '@/models/loadingState';
import * as Sentry from '@sentry/vue';

export const useUserStore = defineStore('user', () => {
  const user = ref<UserModel | undefined>();

  const isGettingUserDetails = reactive(new LoadingStateModel({isLoading: true}));
  const getUser = async (identifier: string) => {
    try {
      isGettingUserDetails.startLoading();
      user.value = await UserService.getUserDetails(identifier);

      //setting user to the Sentry Global scope
      const scope = Sentry.getGlobalScope();
      scope.setUser({
        id: user.value?.identifier,
        email: user.value?.email
      });
    } catch (error) {
      isGettingUserDetails.setError(error);
    } finally {
      isGettingUserDetails.stopLoadingAndFinish();
    }
  };

  const userLink = computed<SidebarLinkModel>(() => ({
    id: SidebarLinkIdentifiers.USER_PROFILE,
    label: user.value ? user.value?.name : 'Loading...',
    icon: user.value ? user.value?.avatar : 'Loading...',
    iconStyle: 'fal',
    selected: false,
    route: '',
    routeEnabled: false,
    loading: isGettingUserDetails.isLoading
  }));

  return {
    user,
    getUser,
    userLink
  };
});
